import store from '../store';
import Events from '../constants/events';
import { handleError } from '../utilities';
import type { AllowState } from '../types/types';

declare global {
  interface Window {
    wf?: {
      ready: (callback: () => void) => void;
      allowUserTracking: (options?: { activate?: boolean }) => void;
      denyUserTracking: (options?: { reload?: boolean }) => void;
      getUserTrackingChoice: () => boolean | null;
    };
  }
}

function detectWebflowTrackingAPI(): boolean {
  // Detect Webflow's new tracking API (replacing legacy Webflow.analytics.*)
  return !!(
    typeof window.wf === 'object' &&
    typeof window.wf.ready === 'function' &&
    typeof window.wf.allowUserTracking === 'function' &&
    typeof window.wf.denyUserTracking === 'function' &&
    typeof window.wf.getUserTrackingChoice === 'function'
  );
}

function syncConcordToWebflow(): void {
  try {
    const allowState = (store.consentState.allowState ||
      {}) as Partial<AllowState>;
    const analyticsConsent = allowState.analytics;

    if (analyticsConsent === true) {
      // Use new API with activate: true for immediate Optimize variation activation
      window.wf?.allowUserTracking({ activate: true });
    } else if (analyticsConsent === false) {
      // Use new API - defaults to no reload
      window.wf?.denyUserTracking();
    }
  } catch (err) {
    handleError(err as Error);
  }
}

function setupWebflowIntegration(): {
  isWebflowTrackingAPI: boolean;
  syncConcordToWebflow: () => void;
} | null {
  try {
    // Use wf.ready() to ensure tracking management endpoint is available (REQUIRED)
    window.wf?.ready(() => {
      // Initialize current Concord state in Webflow with immediate activation
      syncConcordToWebflow();

      // Listen for Concord consent changes and sync to Webflow
      window.addEventListener(Events.ConsentStateChanged, syncConcordToWebflow);
    });

    return {
      isWebflowTrackingAPI: true,
      syncConcordToWebflow,
    };
  } catch (err) {
    handleError(err instanceof Error ? err : new Error(String(err)));
    return null;
  }
}

export function initializeWebflow(): {
  isWebflowTrackingAPI: boolean;
  syncConcordToWebflow: () => void;
} | null {
  // Try immediate detection first
  if (detectWebflowTrackingAPI()) {
    return setupWebflowIntegration();
  }

  // If not available, listen for DOM ready and try again
  const initWhenReady = () => {
    if (detectWebflowTrackingAPI()) {
      setupWebflowIntegration();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady, {
      once: true,
    });
  } else {
    // DOM is already ready, try a short delay
    setTimeout(initWhenReady, 100);
  }

  return null;
}
